import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle, ArrowLeft, History, Printer, Trash2, FileText, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function lastDay(y, m) { return new Date(y, m, 0).getDate(); }
function fmtDate(y, m, d) {
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}
function prettyPeriod(year, month, period) {
  const m = MONTHS[(month || 1) - 1]?.slice(0, 3) || "";
  if (period === "first_half") return `${m} ${year} · 1st Half`;
  if (period === "second_half") return `${m} ${year} · 2nd Half`;
  return `${m} ${year} · Monthly`;
}
function coveredDates(year, month, period) {
  if (period === "first_half") return `${fmtDate(year, month, 1)} – ${fmtDate(year, month, 15)}`;
  if (period === "second_half") return `${fmtDate(year, month, 16)} – ${fmtDate(year, month, lastDay(year, month))}`;
  return `${fmtDate(year, month, 1)} – ${fmtDate(year, month, lastDay(year, month))}`;
}

export default function PaySlipOverviewHistoryPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";

  const today = new Date();
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // currently viewed snapshot
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    if (!selectedBranch) { setSnapshots([]); return; }
    setLoading(true);
    try {
      const params = { branch_id: selectedBranch.id };
      if (yearFilter !== "all") params.year = yearFilter;
      if (monthFilter !== "all") params.month = monthFilter;
      if (periodFilter !== "all") params.period = periodFilter;
      const { data } = await api.get("/payslip/overview/history", { params });
      setSnapshots(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, yearFilter, monthFilter, periodFilter]);

  useEffect(() => { load(); }, [load]);

  const yearOptions = useMemo(() => {
    const out = new Set();
    snapshots.forEach((s) => out.add(s.year));
    if (out.size === 0) {
      const y = today.getFullYear();
      for (let i = y - 3; i <= y + 1; i++) out.add(i);
    }
    return Array.from(out).sort((a, b) => b - a);
  }, [snapshots, today]);

  const doDelete = async (id) => {
    try {
      await api.delete(`/payslip/overview/history/${id}`);
      toast.success("Snapshot removed");
      setConfirmDelete(null);
      if (selected?.id === id) setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------- Render -------
  return (
    <>
      {/* Page header */}
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center">
            <History className="w-6 h-6 text-[#2563EB]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">
              Pay Slip Overview History
            </h1>
            <p className="text-brand-inkMuted mt-0.5 text-sm">
              Immutable snapshots of every Pay Slip Overview saved by your team. Past records stay locked in time.
            </p>
          </div>
        </div>
        {selected && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => window.print()}
              className="rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white shadow-sm h-11 px-5"
              data-testid="print-snapshot-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Print Snapshot
            </Button>
            <Button variant="outline" onClick={() => setSelected(null)}
              className="rounded-2xl border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] h-11 px-5"
              data-testid="back-to-list-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to list
            </Button>
          </div>
        )}
      </div>

      {!selectedBranch ? <NoBranchCard /> : selected ? (
        <SnapshotView snapshot={selected} currency={currency} />
      ) : (
        <>
          {/* Filter bar */}
          <section className="bg-white rounded-2xl border border-[#2563EB]/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="history-filters">
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Branch">
                <select value={selectedBranch.id}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="ovh-select" data-testid="history-branch-select">
                  {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Year">
                <select value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
                  className="ovh-select" data-testid="history-year-select">
                  <option value="all">All years</option>
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
              <Field label="Month">
                <select value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
                  className="ovh-select" data-testid="history-month-select">
                  <option value="all">All months</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="Period">
                <select value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  className="ovh-select" data-testid="history-period-select">
                  <option value="all">All periods</option>
                  <option value="first_half">1st Half (1–15)</option>
                  <option value="second_half">2nd Half (16–End)</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
            </div>
          </section>

          {/* List */}
          {loading ? (
            <p className="text-brand-inkMuted">Loading snapshots...</p>
          ) : snapshots.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#2563EB]/10 p-10 text-center" data-testid="history-empty">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#DBEAFE] flex items-center justify-center mb-3">
                <FileText className="w-8 h-8 text-[#2563EB]" />
              </div>
              <p className="font-heading text-lg font-semibold text-foreground">No saved snapshots yet</p>
              <p className="text-brand-inkMuted text-sm mt-1 max-w-md mx-auto">
                Open <span className="font-semibold text-foreground">Pay Slip Overview</span>, choose a period, and click <span className="font-semibold text-foreground">Save Snapshot</span> to create your first immutable record.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="history-list">
              {snapshots.map((s) => (
                <SnapshotRow
                  key={s.id}
                  snap={s}
                  currency={currency}
                  onOpen={() => setSelected(s)}
                  onDelete={() => setConfirmDelete(s)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete this snapshot?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the immutable record for{" "}
              <span className="font-semibold text-foreground">
                {confirmDelete && prettyPeriod(confirmDelete.year, confirmDelete.month, confirmDelete.period)}
              </span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-[#2563EB]/30">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && doDelete(confirmDelete.id)}
              className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="confirm-delete-snapshot-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <style>{`
        .ovh-select {
          width: 100%; height: 2.5rem; border-radius: 0.875rem;
          border: 1px solid rgba(37, 99, 235, 0.18); background: #fff;
          padding: 0 0.625rem; font-size: 0.875rem; color: hsl(var(--foreground));
        }
        .ovh-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-heading text-brand-inkMuted">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SnapshotRow({ snap, currency, onOpen, onDelete }) {
  const rows = snap?.snapshot?.rows || [];
  const grossTotal = rows.reduce((a, r) => a + (r.gross_pay || 0), 0);
  const netTotal = rows.reduce((a, r) => a + (r.net_pay || 0), 0);
  const fmt = (n) => `${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <li
      className="group bg-white rounded-2xl border border-[#2563EB]/10 shadow-sm hover:shadow-md hover:border-[#2563EB]/30 transition-all overflow-hidden"
      data-testid={`history-row-${snap.id}`}>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-4 sm:p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-[#2563EB]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading text-base sm:text-lg font-semibold text-foreground">
              {prettyPeriod(snap.year, snap.month, snap.period)}
            </p>
            <span className="inline-flex items-center rounded-full bg-[#DBEAFE] text-[#1D4ED8] px-2.5 py-0.5 text-[11px] font-semibold">
              {rows.length} employee{rows.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="text-xs text-brand-inkMuted mt-1">
            {coveredDates(snap.year, snap.month, snap.period)}
            {" · "}
            Saved {new Date(snap.saved_at).toLocaleString()} by {snap.saved_by_name || "—"}
          </p>
          {snap.note && (
            <p className="text-xs text-brand-inkMuted mt-1 italic truncate">{snap.note}</p>
          )}
        </div>
        <div className="hidden sm:flex flex-col items-end shrink-0 mr-1">
          <p className="text-[10px] font-heading uppercase tracking-wider text-brand-inkMuted">Net Total</p>
          <p className="font-heading text-base font-bold text-[#2563EB]">{fmt(netTotal)}</p>
          <p className="text-[10px] text-brand-inkMuted">Gross {fmt(grossTotal)}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-brand-inkMuted group-hover:text-[#2563EB] transition-colors shrink-0" />
      </button>
      <div className="px-4 sm:px-5 pb-3 -mt-1 flex items-center justify-end">
        <Button
          variant="outline" size="sm"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="rounded-full border-[#C62828]/30 text-[#C62828] hover:bg-[#FFEBEE] text-xs h-8"
          data-testid={`delete-snapshot-${snap.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
      </div>
    </li>
  );
}

function SnapshotView({ snapshot, currency }) {
  const data = snapshot?.snapshot;
  const fmt = (v) => `${currency} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const incomeColSpan = (col) => (col.has_breakdown ? 3 : 1);

  // Grand totals (mirror PaySlipOverviewPage)
  const totals = useMemo(() => {
    if (!data) return null;
    const t = {
      by_label: {},
      overtime_total: 0,
      by_deduction: {},
      by_other_deduction: {},
      other_deductions_total: 0,
      gross_pay: 0, net_pay: 0,
    };
    for (const col of (data.income_columns || [])) {
      t.by_label[col.id] = col.has_breakdown
        ? { normal_working_day: 0, regular_holiday: 0, special_holiday: 0, subtotal: 0 }
        : 0;
    }
    for (const r of (data.rows || [])) {
      for (const col of (data.income_columns || [])) {
        const v = r.by_label?.[col.id];
        if (col.has_breakdown) {
          const cur = t.by_label[col.id];
          cur.normal_working_day += (v?.normal_working_day || 0);
          cur.regular_holiday += (v?.regular_holiday || 0);
          cur.special_holiday += (v?.special_holiday || 0);
          cur.subtotal += (v?.subtotal || 0);
        } else {
          t.by_label[col.id] = (t.by_label[col.id] || 0) + (v || 0);
        }
      }
      t.overtime_total += r.overtime_total || 0;
      for (const l of (data.deduction_labels || [])) t.by_deduction[l.id] = (t.by_deduction[l.id] || 0) + (r.by_deduction?.[l.id] || 0);
      for (const n of (data.other_deduction_names || [])) t.by_other_deduction[n] = (t.by_other_deduction[n] || 0) + (r.by_other_deduction?.[n] || 0);
      t.other_deductions_total += r.other_deductions_total || 0;
      t.gross_pay += r.gross_pay || 0;
      t.net_pay += r.net_pay || 0;
    }
    return t;
  }, [data]);

  if (!data) {
    return (
      <div className="bg-white rounded-2xl border border-[#2563EB]/10 p-10 text-center">
        <p className="font-heading font-semibold text-foreground">Snapshot is empty</p>
        <p className="text-brand-inkMuted text-sm mt-1">No data was captured at save time.</p>
      </div>
    );
  }

  return (
    <>
      {/* Hero / meta */}
      <section className="bg-gradient-to-br from-[#EFF6FF] via-white to-[#DBEAFE]/40 border border-[#2563EB]/15 rounded-2xl p-5 mb-4 shadow-sm" data-testid="snapshot-meta">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-heading text-brand-inkMuted uppercase tracking-wider">Period</p>
            <p className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">
              {prettyPeriod(snapshot.year, snapshot.month, snapshot.period)}
            </p>
            <p className="text-sm text-brand-inkMuted mt-0.5">
              Covered Dates: <span className="font-heading font-semibold text-foreground">{coveredDates(snapshot.year, snapshot.month, snapshot.period)}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs font-heading text-brand-inkMuted uppercase tracking-wider">Snapshot saved</p>
            <p className="font-heading text-base font-semibold text-foreground">
              {new Date(snapshot.saved_at).toLocaleString()}
            </p>
            <p className="text-xs text-brand-inkMuted">by {snapshot.saved_by_name || "—"}</p>
          </div>
        </div>
        {snapshot.note && (
          <p className="text-sm text-brand-inkMuted italic mt-3 border-t border-[#2563EB]/10 pt-2">{snapshot.note}</p>
        )}
      </section>

      {/* Overview table — identical structure to PaySlipOverviewPage */}
      {(data.rows || []).length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#2563EB]/10 p-10 text-center" data-testid="snapshot-empty">
          <p className="font-heading font-semibold text-foreground">This snapshot has no employees.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#2563EB]/10 shadow-sm ovh-scroll" data-testid="snapshot-table">
          <table className="ovh-table text-xs sm:text-sm border-collapse">
            <thead className="bg-gradient-to-b from-[#EEF3FF] to-[#F6F8FE] text-brand-inkMuted">
              <tr>
                <th rowSpan={2} className="ovh-th-name">EMPLOYEE NAME</th>
                {(data.income_columns || []).map((col) => (
                  <th key={col.id} colSpan={incomeColSpan(col)} className="ovh-th-group ovh-th-income">
                    <div className="ovh-group-title">{col.name?.toUpperCase()}</div>
                    <div className="ovh-group-sub">{col.schedule}</div>
                  </th>
                ))}
                <th rowSpan={2} className="ovh-th-leaf ovh-th-income">
                  <div className="ovh-group-title">OVERTIME PAY</div>
                  <div className="ovh-group-sub">Across all daily columns</div>
                </th>
                {(data.deduction_labels || []).length > 0 && (
                  <th colSpan={data.deduction_labels.length} className="ovh-th-group ovh-th-gov">
                    <div className="ovh-group-title">GOVERNMENT DEDUCTIONS</div>
                    <div className="ovh-group-sub">Apply this period</div>
                  </th>
                )}
                {(data.other_deduction_names || []).length > 0 && (
                  <th colSpan={data.other_deduction_names.length} className="ovh-th-group ovh-th-other">
                    <div className="ovh-group-title">OTHER DEDUCTIONS</div>
                    <div className="ovh-group-sub">Salary-deducted</div>
                  </th>
                )}
                <th rowSpan={2} className="ovh-th-leaf ovh-th-gross">
                  <div className="ovh-group-title">GROSS PAY</div>
                  <div className="ovh-group-sub">Total</div>
                </th>
                <th rowSpan={2} className="ovh-th-leaf ovh-th-net">
                  <div className="ovh-group-title">NET PAY</div>
                  <div className="ovh-group-sub">Take home</div>
                </th>
              </tr>
              <tr>
                {(data.income_columns || []).map((col) => (
                  col.has_breakdown ? (
                    <React.Fragment key={`${col.id}-sub`}>
                      <th className="ovh-th-sub">Normal Working Day</th>
                      <th className="ovh-th-sub">Regular Holiday</th>
                      <th className="ovh-th-sub">Special Holiday</th>
                    </React.Fragment>
                  ) : (
                    <th key={`${col.id}-sub`} className="ovh-th-sub">Amount</th>
                  )
                ))}
                {(data.deduction_labels || []).map((l) => (
                  <th key={l.id} className="ovh-th-sub">{l.name?.toUpperCase()}</th>
                ))}
                {(data.other_deduction_names || []).map((n) => (
                  <th key={n} className="ovh-th-sub">{n?.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.employee_id} className={`ovh-row ${i % 2 ? "ovh-row-odd" : "ovh-row-even"}`} data-testid={`snapshot-row-${r.employee_id}`}>
                  <td className="ovh-td-name">{r.employee_name}</td>
                  {(data.income_columns || []).map((col) => {
                    const v = r.by_label?.[col.id];
                    if (col.has_breakdown) {
                      return (
                        <React.Fragment key={`${r.employee_id}-${col.id}`}>
                          <td className="ovh-td">{fmt(v?.normal_working_day)}</td>
                          <td className="ovh-td">{fmt(v?.regular_holiday)}</td>
                          <td className="ovh-td">{fmt(v?.special_holiday)}</td>
                        </React.Fragment>
                      );
                    }
                    return <td key={`${r.employee_id}-${col.id}`} className="ovh-td">{fmt(v)}</td>;
                  })}
                  <td className="ovh-td ovh-td-accent">{fmt(r.overtime_total)}</td>
                  {(data.deduction_labels || []).map((l) => (
                    <td key={l.id} className="ovh-td">{fmt(r.by_deduction?.[l.id])}</td>
                  ))}
                  {(data.other_deduction_names || []).map((n) => (
                    <td key={n} className="ovh-td">{fmt(r.by_other_deduction?.[n])}</td>
                  ))}
                  <td className="ovh-td ovh-td-gross">{fmt(r.gross_pay)}</td>
                  <td className="ovh-td ovh-td-net">{fmt(r.net_pay)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {totals && (
                <tr className="ovh-foot" data-testid="snapshot-grand-total">
                  <td className="ovh-td-name ovh-td-foot">Grand Total</td>
                  {(data.income_columns || []).map((col) => {
                    const v = totals.by_label[col.id];
                    if (col.has_breakdown) {
                      return (
                        <React.Fragment key={`tot-${col.id}`}>
                          <td className="ovh-td ovh-td-foot">{fmt(v?.normal_working_day)}</td>
                          <td className="ovh-td ovh-td-foot">{fmt(v?.regular_holiday)}</td>
                          <td className="ovh-td ovh-td-foot">{fmt(v?.special_holiday)}</td>
                        </React.Fragment>
                      );
                    }
                    return <td key={`tot-${col.id}`} className="ovh-td ovh-td-foot">{fmt(v)}</td>;
                  })}
                  <td className="ovh-td ovh-td-foot ovh-td-accent">{fmt(totals.overtime_total)}</td>
                  {(data.deduction_labels || []).map((l) => (
                    <td key={l.id} className="ovh-td ovh-td-foot">{fmt(totals.by_deduction[l.id])}</td>
                  ))}
                  {(data.other_deduction_names || []).map((n) => (
                    <td key={n} className="ovh-td ovh-td-foot">{fmt(totals.by_other_deduction[n])}</td>
                  ))}
                  <td className="ovh-td ovh-td-foot ovh-td-gross">{fmt(totals.gross_pay)}</td>
                  <td className="ovh-td ovh-td-foot ovh-td-net">{fmt(totals.net_pay)}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}

      <style>{`
        .ovh-scroll {
          overflow-x: auto; overflow-y: hidden;
          scrollbar-width: thin; scrollbar-color: #2563eb #E6ECF8;
        }
        .ovh-scroll::-webkit-scrollbar { height: 12px; }
        .ovh-scroll::-webkit-scrollbar-track { background: #EEF3FF; border-radius: 0 0 1rem 1rem; }
        .ovh-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #2563eb, #4f7cf5);
          border-radius: 999px; border: 2px solid #EEF3FF;
        }
        .ovh-scroll::-webkit-scrollbar-thumb:hover { background: #1d4ed8; }

        .ovh-table { width: max-content; min-width: 100%; border-collapse: separate; border-spacing: 0; }
        .ovh-th-group, .ovh-th-leaf, .ovh-th-name, .ovh-th-sub {
          font-family: var(--font-heading), inherit;
          font-weight: 700; letter-spacing: 0.04em; white-space: nowrap;
        }
        .ovh-th-group { text-align: center; padding: 0.625rem 0.875rem; font-size: 11px; line-height: 1.2;
          border-left: 1px solid rgba(37,99,235,0.10); border-bottom: 1px solid rgba(37,99,235,0.10); }
        .ovh-th-leaf { text-align: center; padding: 0.75rem 0.875rem; font-size: 11px; line-height: 1.2;
          border-left: 1px solid rgba(37,99,235,0.10); border-bottom: 1px solid rgba(37,99,235,0.10); }
        .ovh-th-income { color: #047857; }
        .ovh-th-gov { color: #b91c1c; }
        .ovh-th-other { color: #2563eb; }
        .ovh-th-gross { color: #0f172a; }
        .ovh-th-net { color: #2563eb; }
        .ovh-group-title { font-size: 11px; }
        .ovh-group-sub { margin-top: 2px; font-size: 10px; font-weight: 400; letter-spacing: 0; color: #94a3b8; text-transform: none; }

        .ovh-th-name {
          text-align: left; padding: 0.625rem 1rem; font-size: 11px; color: #475569;
          background: linear-gradient(180deg, #EEF3FF, #F6F8FE);
          position: sticky; left: 0; z-index: 3;
          border-right: 1px solid rgba(37,99,235,0.12);
          border-bottom: 1px solid rgba(37,99,235,0.10);
          min-width: 200px; box-shadow: 6px 0 14px -10px rgba(15,23,42,0.18);
        }
        .ovh-th-sub {
          text-align: center; padding: 0.5rem 0.875rem; font-size: 10px; color: #64748b;
          background: #F6F8FE;
          border-left: 1px solid rgba(37,99,235,0.08);
          border-bottom: 1px solid rgba(37,99,235,0.10);
          min-width: 110px;
        }

        .ovh-row td { border-bottom: 1px solid rgba(37,99,235,0.06); transition: background-color 0.15s ease; }
        .ovh-row-even { background: #FAFBFE; }
        .ovh-row-odd { background: #FFFFFF; }
        .ovh-row:hover td { background: #F1F5FF; }
        .ovh-row:hover .ovh-td-name { background: #F1F5FF; }

        .ovh-td {
          text-align: center; padding: 0.875rem 0.875rem; font-size: 13px; color: #1e293b;
          white-space: nowrap; font-variant-numeric: tabular-nums;
          border-left: 1px solid rgba(37,99,235,0.05); min-width: 110px;
        }
        .ovh-td-accent { color: #047857; font-weight: 600; }
        .ovh-td-gross { font-weight: 700; color: #0f172a; }
        .ovh-td-net { font-weight: 700; color: #2563eb; }
        .ovh-td-name {
          text-align: left; padding: 0.875rem 1rem;
          font-family: var(--font-heading), inherit;
          font-weight: 600; font-size: 12.5px; letter-spacing: 0.02em; color: #0f172a;
          text-transform: uppercase; white-space: nowrap;
          position: sticky; left: 0; z-index: 2;
          background: inherit; border-right: 1px solid rgba(37,99,235,0.08);
          box-shadow: 6px 0 14px -10px rgba(15,23,42,0.18); min-width: 200px;
        }
        .ovh-foot td {
          background: linear-gradient(180deg, #EEF3FF, #E2EAFB);
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 13px; color: #0f172a;
          border-top: 1.5px solid rgba(37,99,235,0.25); border-bottom: none;
        }
        .ovh-td-foot { padding-top: 1rem; padding-bottom: 1rem; }

        @media print {
          aside, header, .no-print { display: none !important; }
          body { background: #fff !important; }
          .ovh-scroll { overflow: visible !important; }
          .ovh-td-name, .ovh-th-name { box-shadow: none !important; }
        }
      `}</style>
    </>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">Pay Slip History is scoped per branch.</p>
    </div>
  );
}
